import { useMemo, useState } from "react";
import "./index.css";
import { AuroraText } from "@/components/ui/aurora-text";
import { WordRotate } from "@/components/ui/word-rotate";
import { LightRays } from "@/components/ui/light-rays";
import { ShineBorder } from "@/components/ui/shine-border";

// Simple Icons SVG paths with brand colors
const XIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z" />
  </svg>
);

const ThreadsIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z" />
  </svg>
);

const InstagramIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077" />
  </svg>
);

const YouTubeIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const KickIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M1.333 0h8v5.333H12V2.667h2.667V0h8v8H20v2.667h-2.667v2.666H20V16h2.667v8h-8v-2.667H12v-2.666H9.333V24h-8Z" />
  </svg>
);

const TikTokIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const BlueskyIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.202 2.857C7.954 4.922 10.913 9.11 12 11.358c1.087-2.247 4.046-6.436 6.798-8.501C20.783 1.366 24 .213 24 3.883c0 .732-.42 6.156-.667 7.037-.856 3.061-3.978 3.842-6.755 3.37 4.854.826 6.089 3.562 3.422 6.299-5.065 5.196-7.28-1.304-7.847-2.97-.104-.305-.152-.448-.153-.327 0-.121-.05.022-.153.327-.568 1.666-2.782 8.166-7.847 2.97-2.667-2.737-1.432-5.473 3.422-6.3-2.777.473-5.899-.308-6.755-3.369C.42 10.04 0 4.615 0 3.883c0-3.67 3.217-2.517 5.202-1.026" />
  </svg>
);

const TwitchIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
  </svg>
);

const DiscordIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

// SMOKING SUNDAY logo - simplified wordmark
const SmokingSundayIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 111.1236 60.871264" fill="currentColor">
    <g transform="translate(2.6458,-22.480368)">
      <g transform="translate(0,25.126168)">
        <g transform="scale(0.21243247)">
          <path d="M 37.394043,91.01569 V 67.203445 q 0,-2.822191 -1.058322,-3.704126 -1.058322,-1.058322 -3.880514,-1.058322 H 18.16786 Q 4.5776368e-8,62.440997 4.5776368e-8,44.802297 V 17.462313 Q 4.5776368e-8,9.4604492e-7 19.049795,9.4604492e-7 h 19.40257 q 19.049795,0 19.049795,17.46231205395508 V 32.984369 H 37.041269 V 20.284505 q 0,-2.822192 -1.058322,-3.704127 -1.058322,-1.058322 -3.880514,-1.058322 h -6.702706 q -2.998579,0 -4.056901,1.058322 -1.058322,0.881935 -1.058322,3.704127 v 22.401148 q 0,2.822192 1.058322,3.880514 1.058322,0.881935 4.056901,0.881935 H 39.3343 q 18.520634,0 18.520634,17.285926 v 29.103853 q 0,17.462309 -19.226182,17.462309 H 19.578956 q -19.22618197,0 -19.22618197,-17.462309 V 78.492213 H 20.637278 V 91.01569 q 0,2.822191 1.058322,3.880513 1.058322,0.881935 4.056901,0.881935 h 6.702706 q 2.822192,0 3.880514,-0.881935 1.058322,-1.058322 1.058322,-3.880513 z" />
          <path d="M 90.387307,111.30019 H 72.395834 V 9.4604492e-7 H 94.620595 L 113.31762,66.145123 132.01464,9.4604492e-7 H 154.2394 V 111.30019 H 136.42431 V 33.160756 l -14.8165,53.97442 h -16.404 L 90.387307,33.337143 Z" />
          <path d="m 189.78965,9.4604492e-7 h 21.51922 q 19.22618,0 19.22618,17.46231205395508 v 76.375568 q 0,17.462309 -19.22618,17.462309 h -21.51922 q -19.22618,0 -19.22618,-17.462309 V 17.462313 Q 170.56347,9.4604492e-7 189.78965,9.4604492e-7 Z M 210.25054,91.01569 V 20.284505 q 0,-2.822192 -1.05832,-3.704127 -1.05832,-1.058322 -3.88051,-1.058322 h -9.34851 q -2.99858,0 -4.0569,1.058322 -1.05833,0.881935 -1.05833,3.704127 V 91.01569 q 0,2.822191 1.05833,3.880513 1.05832,0.881935 4.0569,0.881935 h 9.34851 q 2.82219,0 3.88051,-0.881935 1.05832,-1.058322 1.05832,-3.880513 z" />
          <path d="M 312.32348,9.4604492e-7 289.39317,55.385517 312.14709,111.30019 H 290.27511 L 268.22673,55.385517 290.45149,9.4604492e-7 Z M 247.06029,111.30019 V 9.4604492e-7 H 267.3448 V 111.30019 Z" />
          <path d="M 323.22089,9.4604492e-7 H 343.5054 V 111.30019 h -20.28451 z" />
          <path d="m 404.47465,9.4604492e-7 h 18.52063 V 111.30019 H 403.94549 L 380.13325,37.570431 V 111.30019 H 361.43622 V 9.4604492e-7 h 19.57896 L 404.47465,72.671442 Z" />
          <path d="M 498.19126,34.219078 H 477.73037 V 19.578957 q 0,-2.822192 -1.05832,-3.704127 -1.05832,-1.058322 -3.88051,-1.058322 h -8.29019 q -2.82219,0 -3.88051,1.058322 -1.05833,0.881935 -1.05833,3.704127 v 72.318668 q 0,2.822191 1.05833,3.880513 1.05832,0.881935 3.88051,0.881935 h 8.29019 q 2.82219,0 3.88051,-0.881935 1.05832,-1.058322 1.05832,-3.880513 v -24.69418 h -8.81935 V 52.916099 h 29.28024 v 41.098169 q 0,17.462312 -19.22618,17.462312 H 458.3278 q -19.22618,0 -19.22618,-17.462312 V 17.462313 Q 439.10162,9.4604492e-7 458.3278,9.4604492e-7 h 20.63728 q 19.22618,0 19.22618,17.46231205395508 z" />
        </g>
        <g transform="matrix(0.23787798,0,0,0.23787798,-2.3774731e-7,10.222473)">
          <path d="m 37.394044,170.38969 v -23.81224 q 0,-2.8222 -1.058322,-3.70413 Q 35.2774,141.815 32.455208,141.815 H 18.167861 Q 9.9945068e-7,141.815 9.9945068e-7,124.1763 V 96.836314 q 0,-17.462313 19.04979500054932,-17.462313 h 19.40257 q 19.049795,0 19.049795,17.462313 V 112.35837 H 37.04127 V 99.658506 q 0,-2.822192 -1.058322,-3.704127 -1.058322,-1.058322 -3.880514,-1.058322 h -6.702706 q -2.998579,0 -4.056901,1.058322 -1.058322,0.881935 -1.058322,3.704127 v 22.401144 q 0,2.8222 1.058322,3.88052 1.058322,0.88193 4.056901,0.88193 h 13.934573 q 18.520634,0 18.520634,17.28593 v 29.10385 q 0,17.46231 -19.226182,17.46231 H 19.578957 q -19.22618201,0 -19.22618201,-17.46231 V 157.86621 H 20.637279 v 12.52348 q 0,2.82219 1.058322,3.88051 1.058322,0.88194 4.056901,0.88194 h 6.702706 q 2.822192,0 3.880514,-0.88194 1.058322,-1.05832 1.058322,-3.88051 z" />
          <path d="m 111.90652,79.374001 h 20.28451 v 93.837879 q 0,17.46231 -19.22619,17.46231 H 91.44563 q -19.226182,0 -19.226182,-17.46231 V 79.374001 h 20.284504 v 91.015689 q 0,2.82219 1.058322,3.88051 1.058322,0.88194 3.880514,0.88194 h 9.348512 q 2.99858,0 4.0569,-0.88194 1.05832,-1.05832 1.05832,-3.88051 z" />
          <path d="M 191.91456,79.374001 H 210.4352 V 190.67419 h -19.0498 l -23.81224,-73.72976 v 73.72976 H 148.87614 V 79.374001 h 19.57895 l 23.45947,72.671439 z" />
          <path d="m 228.3054,79.374001 h 42.33288 q 19.22618,0 19.22618,17.462313 v 76.375566 q 0,17.46231 -19.22618,17.46231 H 228.3054 Z m 41.09817,91.721239 V 98.952958 q 0,-2.822192 -1.05832,-3.704127 -1.05832,-1.058322 -3.88051,-1.058322 h -15.87483 v 81.667181 h 15.87483 q 2.82219,0 3.88051,-0.88194 1.05832,-1.05832 1.05832,-3.88051 z" />
          <path d="m 351.65503,190.67419 -4.23329,-23.10669 h -24.3414 l -3.88051,23.10669 H 298.91532 L 321.31647,79.374001 h 27.33998 l 23.45947,111.300189 z m -25.92889,-38.45236 h 18.87341 l -9.70128,-53.268872 z" />
          <path d="m 420.38258,190.67419 h -20.2845 V 155.3968 L 374.16919,79.374001 h 20.63728 l 15.16928,57.149389 14.28735,-57.149389 h 20.63727 l -24.51779,75.493629 z" />
        </g>
      </g>
    </g>
  </svg>
);

// Shuffle array helper (runs once on load for random start)
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i];
    shuffled[i] = shuffled[j]!;
    shuffled[j] = temp!;
  }
  return shuffled;
}

// Phrases for the word rotate
const rotatingPhrases = [
  "obsess over tornadoes",
  "play sloppy guitar",
  "belt out powerful vocals",
  "write versatile songs",
  "advocate for free speech",
  "fight against censorship",
  "am a wannabe storm chaser",
  "love dad jokes",
  "stay straight-up Dutch",
  "am SMOKING SUNDAY",
  "stir up controversy",
  "drip with sarcasm... never",
  "engineer software slop",
  "ditched designing",
  "stream games live",
  "host engaging podcasts",
  "raise 3 amazing kids",
  "rock my marriage",
  "reside in Japan",
  "dream of a better world",
  "am a family man",
  "grind to get better",
  "laugh at my own fails",
  "balance family and chaos",
  "am always up for a chat"
];

// Social platforms data with brand colors from Simple Icons
const socials = [
  {
    name: "X",
    Icon: XIcon,
    description: "Quick thoughts and hot takes",
    href: "https://x.com/evan_degraaff",
    handle: "@evan_degraaff",
    cta: "Follow me",
    color: "#FFFFFF",
    shineColors: ["#333333", "#666666", "#999999"],
  },
  {
    name: "YouTube",
    Icon: YouTubeIcon,
    description: "Music, vlogs, and shorts—come watch",
    href: "https://youtube.com/@evandegraaff",
    handle: "@evandegraaff",
    cta: "Subscribe",
    color: "#FF0000",
    shineColors: ["#FF0000", "#CC0000", "#FF3333"],
  },
  {
    name: "Threads",
    Icon: ThreadsIcon,
    description: "Casual convos and updates",
    href: "https://threads.net/@evan.degraaff",
    handle: "@evan.degraaff",
    cta: "Follow me",
    color: "#FFFFFF",
    shineColors: ["#333333", "#666666", "#999999"],
  },
  {
    name: "Instagram",
    Icon: InstagramIcon,
    description: "Daily life, Japan vibes, and behind-the-scenes",
    href: "https://instagram.com/evan.degraaff",
    handle: "@evan.degraaff",
    cta: "Follow me",
    color: "#FF0069",
    shineColors: ["#FF0069", "#E4405F", "#C13584", "#833AB4"],
  },
  {
    name: "Kick",
    Icon: KickIcon,
    description: "Raw gaming and IRL streams",
    href: "https://kick.com/evandegraaff",
    handle: "evandegraaff",
    cta: "Watch live",
    color: "#53FC19",
    shineColors: ["#53FC19", "#3DD912", "#2DB80E"],
  },
  {
    name: "TikTok",
    Icon: TikTokIcon,
    description: "Fun clips and quick moments",
    href: "https://tiktok.com/@evan.de.graaff",
    handle: "@evan.de.graaff",
    cta: "Follow me",
    color: "#FFFFFF",
    shineColors: ["#FF0050", "#00F2EA", "#FFFFFF"],
  },
  {
    name: "Bluesky",
    Icon: BlueskyIcon,
    description: "Chill posting, no drama",
    href: "https://bsky.app/profile/evandegraaff.bsky.social",
    handle: "evandegraaff.bsky.social",
    cta: "Follow me",
    color: "#1185FE",
    shineColors: ["#1185FE", "#0A66C2", "#00A8E8"],
  },
  {
    name: "Twitch",
    Icon: TwitchIcon,
    description: "Join my live game streams and chats",
    href: "https://twitch.tv/evandegraaff",
    handle: "evandegraaff",
    cta: "Watch live",
    color: "#9146FF",
    shineColors: ["#9146FF", "#772CE8", "#B9A3FF"],
  },
];

// Featured large cards (span 2 columns)
const featuredCards = [
  {
    name: "Discord",
    Icon: DiscordIcon,
    description: "Got questions, feedback, or just wanna hang out? Add me on Discord and let's chat!",
    href: "https://discord.com",
    cta: "Add me: wrongtaste",
    color: "#5865F2",
    shineColors: ["#5865F2", "#7289DA", "#99AAB5"],
  },
  {
    name: "SMOKING SUNDAY",
    Icon: SmokingSundayIcon,
    description: "My music project. Original songs, raw vocals, and guitar chaos. Check out my artist side.",
    href: "https://smokingsunday.com/",
    cta: "Listen now",
    color: "#FF6B35",
    shineColors: ["#FF6B35", "#F7931E", "#FFD23F"],
  },
];

export function App() {
  // Shuffle phrases once on mount
  const shuffledPhrases = useMemo(() => shuffleArray(rotatingPhrases), []);
  
  // State for copy button feedback
  const [copyButtonText, setCopyButtonText] = useState("Copy All Links");

  // Handler to copy all social links to clipboard
  const handleCopyAllLinks = async () => {
    const allLinks = [
      ...socials.map(s => `${s.name}: ${s.href}`),
      ...featuredCards
        .filter(c => c.name !== "Discord")
        .map(c => c.name === "SMOKING SUNDAY" ? `My Music: ${c.href}` : `${c.name}: ${c.href}`)
    ].join('\n');
    
    try {
      await navigator.clipboard.writeText(allLinks);
      setCopyButtonText("✓ Copied!");
      setTimeout(() => setCopyButtonText("Copy All Links"), 2000);
    } catch (err) {
      setCopyButtonText("Failed to copy");
      setTimeout(() => setCopyButtonText("Copy All Links"), 2000);
    }
  };

  return (
    <div className="dark min-h-screen w-full bg-slate-950 text-white overflow-hidden">
      {/* Light rays background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <LightRays color="rgba(147, 51, 234, 0.15)" count={5} speed={18} />
      </div>

      {/* Main content */}
      <main className="relative z-10 container mx-auto px-4 py-12 md:py-20">
        {/* Hero Section */}
        <section className="text-center mb-16 md:mb-24">
          {/* Main headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="text-white">Yo, I'm </span>
            <AuroraText
              colors={["#FF0080", "#7928CA", "#0070F3", "#38bdf8"]}
              speed={1.5}
              className="text-4xl md:text-6xl lg:text-7xl font-bold"
            >
              Evan
            </AuroraText>
          </h1>

          {/* Word rotate section */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 text-xl md:text-3xl lg:text-4xl">
            <span className="text-slate-300">and I</span>
            <WordRotate
              words={shuffledPhrases}
              duration={1200}
              className="text-xl md:text-3xl lg:text-4xl font-semibold text-purple-400"
            />
          </div>
        </section>

        {/* Social Links with Shine Border */}
        <section className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Social media cards */}
            {socials.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative rounded-xl bg-slate-900/80 backdrop-blur-sm p-5 transition-all duration-300 hover:scale-[1.02] hover:bg-slate-800/80"
              >
                <ShineBorder
                  shineColor={social.shineColors}
                  borderWidth={2}
                  duration={8}
                  className="rounded-xl"
                />
                
                <div className="flex flex-col gap-3 h-full">
                  {/* Icon */}
                  <social.Icon
                    className="w-8 h-8 transition-transform duration-300 group-hover:scale-110"
                    style={{ color: social.color }}
                  />
                  
                  {/* Handle */}
                  <p className="text-sm text-slate-400 font-mono truncate">
                    {social.handle}
                  </p>
                  
                  {/* Description */}
                  <p className="text-sm text-slate-400">
                    {social.description}
                  </p>
                  
                  {/* CTA */}
                  <span
                    className="mt-auto text-sm font-medium transition-colors"
                    style={{ color: social.color === "#000000" ? "#9ca3af" : social.color }}
                  >
                    {social.cta} →
                  </span>
                </div>
              </a>
            ))}

            {/* Featured cards - span 2 columns */}
            {featuredCards.map((card) => (
              <a
                key={card.name}
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative rounded-xl bg-slate-900/80 backdrop-blur-sm p-6 transition-all duration-300 hover:scale-[1.02] hover:bg-slate-800/80 sm:col-span-2"
              >
                <ShineBorder
                  shineColor={card.shineColors}
                  borderWidth={2}
                  duration={8}
                  className="rounded-xl"
                />
                
                <div className="flex flex-col h-full">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    {/* Icon */}
                    <card.Icon
                      className="w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ color: card.color }}
                    />
                    
                    <div className="flex flex-col gap-2">
                      {/* Name */}
                      <h3 
                        className="text-xl font-bold transition-colors"
                        style={{ color: card.color }}
                      >
                        {card.name}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-sm text-slate-400">
                        {card.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <span
                    className="mt-auto pt-3 text-sm font-medium transition-colors"
                    style={{ color: card.color }}
                  >
                    {card.cta} →
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Copy All Links Button */}
        <section className="max-w-5xl mx-auto mt-12">
          <div className="flex justify-center">
            <button
              onClick={handleCopyAllLinks}
              className="group relative rounded-xl bg-slate-900/80 backdrop-blur-sm px-8 py-4 transition-all duration-300 hover:scale-[1.02] hover:bg-slate-800/80"
            >
              <ShineBorder
                shineColor={["#9333EA", "#7C3AED", "#A855F7"]}
                borderWidth={2}
                duration={8}
                className="rounded-xl"
              />
              <span className="text-purple-400 font-semibold">
                {copyButtonText}
              </span>
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-20 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Evan de Graaff. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
}

export default App;
